import React from 'react';

/**
 * The numbered section card used by the new starter forms.
 *
 * Lifted out of NewStarterPage so the employee-facing form (which the new
 * starter opens from an emailed link, with no account) is visibly the same
 * form, not a lookalike. `total` replaces the page-level SECTION_TOTAL so a
 * four-section form knows when it has reached its last card.
 */
export default function StarterSection({ step, total, label, desc, active, onPrev, onNext, children }) {
  if (!active) return null;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-brand-50 to-transparent dark:from-brand-900/20 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
        <span className="grid place-items-center w-7 h-7 rounded-full bg-brand-600 text-white text-[12px] font-bold">{step + 1}</span>
        <div>
          <h3 className="font-heading text-[18px] text-ink dark:text-gray-100 leading-tight">{label}</h3>
          <p className="text-[12.5px] text-gray-500">{desc}</p>
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
      <div className="px-6 py-3 bg-gray-50/40 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
        <button type="button" disabled={step === 0} onClick={onPrev}
          className="px-3 py-1.5 text-[13px] font-medium text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-30 inline-flex items-center gap-1">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><polyline points="15 18 9 12 15 6" /></svg>
          Previous
        </button>
        <button type="button" onClick={onNext} disabled={step === total - 1}
          className="px-4 py-1.5 text-[13px] font-semibold text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1">
          {step === total - 1 ? 'Last section' : 'Next section'}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      </div>
    </div>
  );
}

/**
 * The progress strip above the sections. Column count follows the number of
 * labels, so the same component serves the seven-section employer form and the
 * four-section employee one.
 */
export function StarterProgress({ labels, done, activeStep, onJump, hint }) {
  const doneCount = done.filter(Boolean).length;
  return (
    <div className="mb-2">
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}>
        {labels.map((label, i) => {
          const isDone = done[i];
          const active = activeStep === i;
          const barCls = active ? 'bg-brand-500' : isDone ? 'bg-emerald-500' : 'bg-gray-200';
          const textCls = active ? 'text-brand-700' : isDone ? 'text-emerald-700' : 'text-gray-500';
          return (
            <button type="button" key={label} onClick={() => onJump(i)}
              className={`text-left rounded-md px-1 py-1.5 -mx-1 transition-colors ${active ? '' : 'hover:bg-gray-50'}`}
              aria-current={active ? 'step' : undefined}>
              <div className={`h-2 rounded-full ${barCls} transition-colors`} />
              <p className={`text-[11.5px] font-semibold mt-1.5 ${textCls}`}>{label}</p>
            </button>
          );
        })}
      </div>
      <p className="text-[11.5px] text-gray-400 mt-1.5">
        {hint || 'Tap any section to jump'} · {doneCount} of {labels.length} complete
      </p>
    </div>
  );
}
